import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { User } from '@angular/fire/auth';
import { IdTokenResult } from 'firebase/auth';
import { UserProfile } from '@core/models/user.model';
import { LoginCredentials, SessionData } from '../models/auth.model';
import { UserService } from '@core/services/user.service';
import { environment } from '@environments/environment';
import { FirebaseAuthService } from './firebase-auth.service';
import { Result } from '@core/models/common.model';
import { APP_ROUTES } from '@core/constants/route.constant';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly platformId = inject(PLATFORM_ID);

  public currentUserProfile = signal<UserProfile | null>(null);

  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public readonly isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private readonly userProfileSubject = new BehaviorSubject<UserProfile | null | undefined>(
    undefined,
  );
  public readonly userProfile$ = this.userProfileSubject.asObservable();

  private readonly sessionSubject = new BehaviorSubject<SessionData | null>(null);
  public readonly session$ = this.sessionSubject.asObservable();

  private readonly sessionStorageKey = 'icxbee_backoffice_session';

  constructor(
    private router: Router,
    private fbAuth: FirebaseAuthService,
    private userService: UserService,
  ) {
    this.restoreSession();
    this.bindAuthHydrationAndProfile();
  }

  private bindAuthHydrationAndProfile(): void {
    this.fbAuth.ready$
      .pipe(
        switchMap(() => this.fbAuth.currentFbUser$),
        distinctUntilChanged((a, b) => (a?.uid ?? null) === (b?.uid ?? null)),
        switchMap((fbUser) => {
          if (!fbUser) {
            this.userProfileSubject.next(null);
            this.isAuthenticatedSubject.next(false);
            this.clearSession();
            return of<Result<UserProfile>>({
              success: false,
              errorCode: 'NO_USER',
              message: 'User is logged out',
            });
          }

          this.userProfileSubject.next(undefined);

          return from(fbUser.getIdTokenResult(true)).pipe(
            tap(async (tokenResult) => {
              const sessionData = await this.createSessionData(fbUser, tokenResult);
              if (tokenResult.token) {
                this.setSession(sessionData);
              }
            }),
            switchMap(() => this.userService.getUserProfileByUid(fbUser.uid)),
            tap((result) => {
              if (result.success) {
                this.userProfileSubject.next(result.data);
                this.currentUserProfile.set(result.data);
                this.isAuthenticatedSubject.next(true);
              } else {
                this.userProfileSubject.next(null);
                this.currentUserProfile.set(null);
                this.isAuthenticatedSubject.next(false);
              }
            }),
            catchError((err) =>
              of<Result<UserProfile>>({
                success: false,
                errorCode: 'BACKEND_ERROR',
                message: err?.message || 'Failed to fetch profile',
              }),
            ),
          );
        }),
      )
      .subscribe((result) => {
        if (result.success && environment.consoleFlag) {
          console.log('[Auth] Profile and session loaded');
        }
      });
  }

  loginWithEmail(data: LoginCredentials): Observable<Result<UserProfile>> {
    return from(
      this.fbAuth.loginWithEmail(data.email, data.password, data.rememberMe ?? false),
    ).pipe(
      mergeMap((loginResult) => {
        if (!loginResult.success) {
          return of<Result<UserProfile>>({
            success: false,
            message: loginResult.message,
            errorCode: loginResult.errorCode,
          });
        }

        return this.userService.getUserProfileByUid(loginResult.data.uid).pipe(
          tap((result) => {
            if (result.success) {
              this.userProfileSubject.next(result.data);
              this.currentUserProfile.set(result.data);
              this.isAuthenticatedSubject.next(true);
            } else {
              this.userProfileSubject.next(null);
              this.currentUserProfile.set(null);
              this.isAuthenticatedSubject.next(false);
            }
          }),
          catchError((err) =>
            of<Result<UserProfile>>({
              success: false,
              message: err?.message || 'Backend error',
              errorCode: 'BACKEND_ERROR',
            }),
          ),
        );
      }),
      catchError((err: { code?: string; message?: string }) =>
        of<Result<UserProfile>>({
          success: false,
          message: err?.code ? this.fbAuth.formatError(err.code) : err?.message || 'Login failed',
          errorCode: err?.code || 'FIREBASE_ERROR',
        }),
      ),
    );
  }

  private async createSessionData(user: User, tokenResult: IdTokenResult): Promise<SessionData> {
    return {
      uid: user.uid,
      email: user.email!,
      token: tokenResult.token,
      lastActivity: Date.now(),
      createdAt: Date.now(),
    };
  }

  private setSession(sessionData: SessionData): void {
    this.sessionSubject.next(sessionData);

    if (isPlatformBrowser(this.platformId)) {
      const storage =
        environment.auth.persistenceType === 'local' ? localStorage : sessionStorage;
      storage.setItem(this.sessionStorageKey, JSON.stringify(sessionData));
    }
  }

  private clearSession(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.sessionStorageKey);
      sessionStorage.removeItem(this.sessionStorageKey);
    }

    this.userProfileSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.sessionSubject.next(null);
  }

  private restoreSession(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const storage =
      environment.auth.persistenceType === 'local' ? localStorage : sessionStorage;
    const storedSession = storage.getItem(this.sessionStorageKey);

    if (!storedSession) return;

    try {
      const sessionData: SessionData = JSON.parse(storedSession);
      if (this.isSessionValid(sessionData)) {
        this.sessionSubject.next(sessionData);
      } else {
        storage.removeItem(this.sessionStorageKey);
      }
    } catch (error) {
      console.error('Error parsing stored session:', error);
      storage.removeItem(this.sessionStorageKey);
    }
  }

  private isSessionValid(sessionData: SessionData): boolean {
    const sessionAge = Date.now() - sessionData.createdAt;
    return sessionAge <= environment.auth.sessionTimeout;
  }

  async logout(): Promise<void> {
    try {
      await this.fbAuth.logout();
      this.clearSession();
      this.currentUserProfile.set(null);
      await this.router.navigate([APP_ROUTES.AUTH.ROOT, APP_ROUTES.AUTH.LOGIN]);
    } catch (error) {
      console.error('Error during logout:', error);
      throw error;
    }
  }

  get authState(): boolean {
    return this.isAuthenticatedSubject.value;
  }
}
